import React, { useCallback, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
// @ts-ignore
import { gtag } from 'ga-gtag'
import { BGVariants } from '../../types/heroTextSection'
import { PackageVariant, SellingPoint } from '../../types/productPageV2'
import { Button as CTA, PurchaseButton } from '../../types/page'
import { CTAClickSuffix, ZipJobConstants } from '../../types/constants'
import Affirm from '../Affirm'
import useEvents from '../../hooks/useEvents'
import RichText from '../RichText'
import { BLOCKS } from '@contentful/rich-text-types'
import checkmarkSVG from '@talentinc/gatsby-theme-ecom/images/checkmark.svg'
import checkmarkWhiteSVG from '@talentinc/gatsby-theme-ecom/images/checkmark-white.svg'
import NavigationGroupLink from '../NavigationGroup/NavigationGroupLink'
import ProductPriceInsert from './ProductPriceInsert'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { appendZjSpecificTokens } from '../../utils/url'
import useDiscountToken from '../../hooks/useDiscountToken'
import { withTwoPassRenderer } from '../../hocs/withTwoPassRenderer'
import { useMixpanelCTAClickTracking } from '../../utils/mixpanel'

export interface PurchaseCardProps {
  title?: string | null
  description: string
  sellingPoints: SellingPoint[]
  cta: PurchaseButton | CTA | null
  backgroundColor: string
  // discountToken: string
  variant: string
  highlightPackage: boolean
  highlightTag: string
  // originalAmount?: number
  amount?: number
  discountedAmount?: number
  formattedAmount?: string
  formattedDiscount?: string | null
  planCode?: string
  purchasePath?: string
  sort?: number | null
  priceOverride?: string
}

const PurchaseCardZipJob: React.FC<PurchaseCardProps> = ({
  title,
  description,
  sellingPoints,
  amount = 0,
  formattedAmount,
  planCode,
  cta,
  backgroundColor,
  variant,
  highlightPackage,
  highlightTag,
  sort,
  formattedDiscount,
  purchasePath,
  priceOverride,
  discountedAmount,
}) => {
  const { classes, cx } = useStyles()
  const { t } = useTranslation()
  const sendEvent = useEvents()
  const discountToken = useDiscountToken()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const eventName = useMemo(
    () =>
      cta?.buttonText.toLowerCase().split(' ').join('_') + CTAClickSuffix ?? '',
    [cta?.buttonText]
  )

  let discount = 0
  if (discountedAmount) {
    discount = amount - discountedAmount
  }

  const sendEventOnSubmit = useCallback(
    async (
      event:
        | React.FormEvent<HTMLFormElement>
        | React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
      event.preventDefault()
      try {
        await sendEvent({
          event: 'Purchase Footer Package Selected',
          variables: {
            add_to_cart: {
              brand: 'ZipJob',
              title,
              amount,
            },
          },
          // @TODO: Clean the commented code once the GA4 config is stable
          // Okay so this event is a little crazy. In the old ZipJob blog, the
          // user would click on services, select a package, and then checkout.
          // In the new ZipJob, this click will skip the services page entirely,
          // which while better UX, breaks a legacy ZipJob event in GTM which
          // seems to be breaking some eventing in GA. The horrible event can be
          // found below by searching for 'Ecommerce_events'.
          // https://tagmanager.google.com/#/container/accounts/4702022732/containers/11986714/workspaces/49/tags
          // gtmPayload: {
          //   event: 'addToCart',
          //   ecommerce: {
          //     currencyCode: 'USD',
          //     add: {
          //       products: {
          //         name: title,
          //         id: title,
          //         category: 'Plans',
          //         amount,
          //         list: 'Services page',
          //         brand: 'ZipJob',
          //         position: sort,
          //       },
          //     },
          //     eventAction: 'Add to Cart',
          //     eventCategory: 'Ecommerce',
          //     eventLabel: title,
          //   },
          // },
        })
        gtag('event', 'add_to_cart', {
          currency: 'USD',
          value: discountedAmount ?? amount,
          items: [
            {
              item_id: planCode,
              item_name: title,
              coupon: discountToken ?? '',
              discount,
              index: sort,
              item_brand: 'ZipJob',
              item_category: 'Plans',
              item_list_name: 'Services page',
              price: amount,
            },
          ],
        })
      } finally {
        //@ts-ignore
        switch (event.target?.tagName) {
          case 'A':
            // @ts-ignore
            window.location.href = event.target.href
            break
          case 'SPAN':
            // @ts-ignore
            window.location.href = event.target.parentNode.href
            break
          case 'FORM':
            // @ts-ignore
            event.target?.submit?.()
            break
        }
      }
    },
    [
      sendEvent,
      title,
      amount,
      sort,
      discount,
      discountToken,
      discountedAmount,
      planCode,
    ]
  )

  return (
    <Card
      className={cx({
        [classes.card]: true,
        [classes.cardMostPopular]: highlightPackage,
        [classes.cardDarkPrimary]: backgroundColor === BGVariants.DarkPrimary,
      })}
      elevation={0}
    >
      {highlightPackage && (
        <Typography
          className={cx({
            [classes.popularBanner]: true,
          })}
          component="p"
          variant="overline"
        >
          {highlightTag}
        </Typography>
      )}
      <CardContent
        className={cx({
          [classes.content]: true,
          [classes.contentExpanded]: variant === PackageVariant.expandedPackage,
        })}
      >
        <Box
          className={cx({
            [classes.expandedHeader]:
              variant === PackageVariant.expandedPackage,
          })}
        >
          <Box
            className={cx({
              [classes.expandedPackage]:
                variant === PackageVariant.expandedPackage,
            })}
          >
            <Typography variant="h5">{title}</Typography>
            <Typography
              className={cx({
                [classes.description]: true,
              })}
              variant="body1"
            >
              {description}
            </Typography>
          </Box>
          <Box>
            {formattedAmount ? (
              <ProductPriceInsert
                formattedAmount={formattedAmount}
                formattedDiscount={formattedDiscount}
                url={purchasePath}
                linkPriceClass={classes.price}
                originalPriceClass={classes.price}
              />
            ) : (
              <>
                {priceOverride ? (
                  <Typography className={classes.price}>
                    {priceOverride}
                  </Typography>
                ) : (
                  <Typography className={classes.price}>
                    {t('purchaseCard.free')}
                  </Typography>
                )}
              </>
            )}
            <Box className={classes.affirm}>
              {formattedAmount && planCode && (
                <Affirm planCode={planCode} price={amount} />
              )}
            </Box>
          </Box>
        </Box>

        <Divider
          className={cx({
            [classes.divider]: true,
            [classes.dividerExpanded]:
              variant === PackageVariant.expandedPackage,
          })}
        />

        <ul
          className={cx({
            [classes.sellingPoints]: true,
            [classes.sellingPointsInPost]: variant === 'inPost',
            [classes.sellingPointsWhite]:
              backgroundColor === BGVariants.DarkPrimary,
            [classes.sellingPointsExpanded]:
              variant === PackageVariant.expandedPackage,
          })}
        >
          {sellingPoints.map((point) => (
            <Typography
              className={classes.sellingPoint}
              component="li"
              key={point.contentful_id}
              variant="body2"
            >
              {point.sellingPointBody && (
                <RichText
                  rawBody={point.sellingPointBody.raw}
                  options={{
                    props: {
                      [BLOCKS.PARAGRAPH]: {
                        className: classes.listItem,
                      },
                    },
                  }}
                />
              )}
            </Typography>
          ))}
        </ul>
        {cta &&
          (planCode ? (
            <form
              method="POST"
              action={appendZjSpecificTokens(purchasePath ?? '')}
              className={classes.purchaseForm}
              // @ts-ignore
              onSubmit={sendEventOnSubmit}
            >
              <input type="hidden" name="_method" value="POST" />
              <input
                type="hidden"
                name="data[Plan][totalPrice]"
                value={amount}
              />
              <input type="hidden" name="data[Plan][pricetype]" value={0} />
              {planCode !== 'B0qd82z' ? (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  type="submit"
                  data-gtm-purchase-button={title}
                  data-qa-purchase-button={title}
                  onAuxClick={(e) => {
                    handleMpCTAClickTracking(
                      e.currentTarget.textContent,
                      purchasePath ?? '',
                      { eventName }
                    )
                  }}
                  onClick={(e) => {
                    handleMpCTAClickTracking(
                      e.currentTarget.textContent,
                      purchasePath ?? '',
                      { eventName }
                    )
                  }}
                >
                  {cta.buttonText}
                </Button>
              ) : (
                // @ts-ignore
                <Button
                  onAuxClick={(e) => {
                    sendEventOnSubmit(e)

                    handleMpCTAClickTracking(
                      e.currentTarget.textContent,
                      purchasePath ?? '',
                      { eventName }
                    )
                  }}
                  onClick={(e) => {
                    sendEventOnSubmit(e)

                    handleMpCTAClickTracking(
                      e.currentTarget.textContent,
                      purchasePath ?? '',
                      { eventName }
                    )
                  }}
                  href={appendZjSpecificTokens(
                    ZipJobConstants.ExecutivePurchaseURL
                  )}
                  fullWidth={variant !== PackageVariant.expandedPackage}
                  data-gtm-purchase-button={title}
                  data-qa-purchase-button={title}
                >
                  {cta.buttonText}
                </Button>
              )}
            </form>
          ) : (
            <NavigationGroupLink key={cta.contentful_id} link={cta} />
          ))}
      </CardContent>
    </Card>
  )
}

const useStyles = makeStyles()((theme) => ({
  affirm: {
    minHeight: '2.1rem',
  },
  card: {
    backgroundColor: theme.palette.background.default,
  },
  cardDarkPrimary: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.background.default,
  },
  cardMostPopular: {
    border: `2px solid ${theme.palette.secondary.dark}`,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '32em',
    padding: '2.28em 2em 1.86em',
    '&:last-child': {
      paddingBottom: '2em',
    },
    [theme.breakpoints.down('md')]: {
      minHeight: 'initial',
    },
  },
  contentInPost: {
    minHeight: '27.5em',
    padding: '1.75em',

    '&:last-child': {
      paddingBottom: '1.75em',
    },

    [theme.breakpoints.down('lg')]: {
      minHeight: 'unset',
    },
  },
  divider: {
    margin: '0.75em 0 1.06em',
  },
  dividerExpanded: {
    margin: '.1em 0 1.06em',
  },
  contentExpanded: {
    minHeight: 'unset',
  },
  description: {
    margin: '.39em 0 1.48em',
  },
  expandedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  expandedPackage: {
    width: '50%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  popularBanner: {
    backgroundColor: theme.palette.secondary.dark,
    color: theme.palette.background.default,
    lineHeight: 'normal',
    padding: '.86em 0 .79em',
    textAlign: 'center',
  },
  purchaseForm: {
    marginTop: 'auto',
    '& > * ': {
      width: '100%',
    },
  },
  listItem: {
    fontSize: '1rem',
    lineHeight: '1.375rem',
  },
  sellingPointsExpanded: {
    columnCount: 2,
    [theme.breakpoints.down('md')]: {
      columnCount: 'unset',
    },
  },
  sellingPoint: {
    padding: '.38em 0 .38em .25em',
  },
  sellingPoints: {
    listStyleImage: `url(${checkmarkSVG})`,
    margin: '0 0 1.34em',
    paddingLeft: '1.2em',
  },

  sellingPointsWhite: {
    listStyleImage: `url(${checkmarkWhiteSVG})`,
  },
  sellingPointsInPost: {
    margin: '0 0 1.25em',
  },

  icon: {
    marginRight: '.1rem',
    width: 'auto',
  },
  listItemIcon: {
    minWidth: '1.45rem',
  },
  price: {
    '&&': {
      fontSize: '1.31em',
      fontFamily: 'century-old-style-std, serif',
      fontWeight: 700,
    },
  },
}))

export default withTwoPassRenderer(PurchaseCardZipJob)
